<template>
  <div
    v-if="note"
    class="user-expiring-note"
    :class="{
      'user-expiring-note--compact': compact,
      'user-expiring-note--filled': highlightFilled && hasNote,
    }"
    @click.stop
  >
    <label class="user-expiring-note__label">
      <span v-if="!compact">{{ label }}</span>
      <input
        v-if="compact"
        type="text"
        :value="note.note"
        :aria-label="`${label} di ${userName || 'utente'}`"
        :disabled="!note.loaded || note.loading || !!note.loadError"
        placeholder="Aggiungi una nota…"
        @input="edit($event.target.value)"
        @blur="flush"
        @keydown.enter.prevent="flush"
      />
      <textarea
        v-else
        :value="note.note"
        :aria-label="`${label} di ${userName || 'utente'}`"
        :disabled="!note.loaded || note.loading || !!note.loadError"
        rows="3"
        placeholder="Aggiungi una nota…"
        @input="edit($event.target.value)"
        @blur="flush"
      ></textarea>
    </label>
    <span v-if="!compact" class="user-expiring-note__hint">Condivise con Monitoraggio percorsi, Scadenze imminenti e Anagrafica · Salvataggio automatico.</span>
    <div v-if="note.conflict" class="user-expiring-note__conflict" role="alert">
      <p>La nota è stata modificata da un’altra pagina o da un altro operatore. La tua bozza è conservata.</p>
      <strong>Versione salvata</strong>
      <pre>{{ note.remoteNote || 'Nessuna nota' }}</pre>
      <div class="user-expiring-note__conflict-actions">
        <button type="button" :disabled="note.saving || note.loading || !!note.loadError" @click="useSavedVersion">Usa versione salvata</button>
        <button type="button" :disabled="note.saving || note.loading || !!note.loadError" @click="saveMyVersion">Salva la mia versione</button>
      </div>
    </div>
    <div v-if="note.loadError" class="user-expiring-note__error" role="alert">
      <span>{{ note.loadError }}</span>
      <button type="button" @click="retryLoad">Riprova caricamento</button>
    </div>
    <div v-if="note.saveError" class="user-expiring-note__error" role="alert">
      <span>{{ note.saveError }}</span>
      <button type="button" :disabled="note.saving" @click="retrySave">Riprova salvataggio</button>
    </div>
    <span v-if="note.loading" class="user-expiring-note__status" role="status">Caricamento note…</span>
    <span v-else-if="note.saving" class="user-expiring-note__status" role="status">Salvataggio…</span>
    <span v-else-if="note.dirty && !note.saveError && !note.conflict" class="user-expiring-note__status" role="status">Modifiche da salvare</span>
    <span v-else-if="note.saved" class="user-expiring-note__status user-expiring-note__status--saved" role="status">Salvato</span>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue';
import { acquireUserExpiringNote } from '@/lib/userExpiringNote';

export default {
  name: 'UserExpiringNote',
  props: {
    userId: { type: String, required: true },
    userName: { type: String, default: '' },
    label: { type: String, default: 'Note' },
    compact: { type: Boolean, default: false },
    highlightFilled: { type: Boolean, default: false },
  },
  setup(props) {
    const note = shallowRef(null);
    const hasNote = computed(() => Boolean(note.value?.note?.trim()));
    let lease = null;
    let stopWatching = null;
    onMounted(() => {
      stopWatching = watch(() => props.userId, userId => {
        lease?.release();
        lease = userId ? acquireUserExpiringNote(userId) : null;
        note.value = lease?.state || null;
      }, { immediate: true });
    });
    onBeforeUnmount(() => {
      stopWatching?.();
      lease?.release();
    });
    return {
      note,
      hasNote,
      edit: value => lease?.edit(value),
      flush: () => lease?.flush(),
      retryLoad: () => lease?.retryLoad(),
      retrySave: () => lease?.retrySave(),
      useSavedVersion: () => lease?.useSavedVersion(),
      saveMyVersion: () => lease?.saveMyVersion(),
    };
  },
};
</script>

<style scoped>
.user-expiring-note { min-width: 0; text-align: left; }
.user-expiring-note__label { display: flex; flex-direction: column; gap: 7px; color: #475569; font-size: 12px; font-weight: 650; }
.user-expiring-note :is(input, textarea) { box-sizing: border-box; width: 100%; min-width: 0; margin: 0; padding: 9px 11px; border: 1px solid #d5dde5; border-radius: 9px; background: #fff; color: #0f172a; font: inherit; font-size: 13px; font-weight: 400; line-height: 1.5; box-shadow: none; }
.user-expiring-note--filled :is(input, textarea) { border: 2px solid #b8842f; background:#fffdf6; box-shadow:0 0 0 3px rgba(184,132,47,.11); }
.user-expiring-note textarea { height: 90px; min-height: 82px; resize: vertical; }
.user-expiring-note :is(input, textarea):focus { outline: 2px solid #93b7a1; outline-offset: 1px; }
.user-expiring-note :is(input, textarea):disabled { background: #f8fafc; color: #64748b; }
.user-expiring-note--compact { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 16px; background: rgba(255, 255, 255, .88); }
.user-expiring-note--compact input { min-height: 38px; border-radius: 12px; }
.user-expiring-note__status { display: block; margin-top: 5px; color: #64748b; font-size: 11px; }
.user-expiring-note__hint { display: block; margin-top: 6px; color: #64748b; font-size: 11px; line-height: 1.5; }
.user-expiring-note__status--saved { color: #287445; }
.user-expiring-note__error { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin-top: 6px; color: #a33d3d; font-size: 12px; overflow-wrap: anywhere; }
.user-expiring-note__error button { margin: 0; padding: 5px 8px; border: 1px solid #dfbebe; border-radius: 6px; background: #fff; color: #9b3030; font: inherit; box-shadow: none; cursor: pointer; }
.user-expiring-note__conflict { box-sizing: border-box; margin-top: 8px; padding: 11px; border: 1px solid #d7b77c; border-radius: 9px; background: #fffbeb; color: #77531c; font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
.user-expiring-note__conflict p { margin: 0 0 8px; }
.user-expiring-note__conflict pre { max-height: 140px; overflow: auto; margin: 5px 0 10px; padding: 8px; border-radius: 6px; background: #fff; color: #475569; font: inherit; white-space: pre-wrap; overflow-wrap: anywhere; }
.user-expiring-note__conflict-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.user-expiring-note__conflict-actions button { min-height: 36px; min-width: 0; margin: 0; padding: 7px 10px; border: 1px solid #d7b77c; border-radius: 6px; background: #fff; color: #77531c; font: inherit; cursor: pointer; white-space: normal; }
.user-expiring-note__conflict-actions button:disabled { opacity: .6; cursor: wait; }
@media (max-width: 640px) { .user-expiring-note :is(input, textarea) { font-size: 16px; } }
</style>
